import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Package,
    FileText,
    Calculator,
    ClipboardList,
    List,
    TrendingUp,
    UserCircle,
    ShoppingCart,
    DollarSign,
    Truck,
    BookOpen,
    Menu,
    LogOut,
    Zap,
    Factory,
    HelpCircle,
    Box,
    Layers,
    Receipt,
    ShoppingBag,
    CreditCard,
    ChevronDown,
    ChevronRight,
    Pin,
    PinOff,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useModuleLoader } from '../../context/ModuleLoaderContext';
import { useNavigationProfile } from '../../hooks/useNavigationProfile';
import { canonicalizeNavigationPath, findNavigationMatchByPath } from '../../lib/navigationPaths';
import { isEnabledFlag } from '../../lib/featureFlags';

type NavItemConfig = {
    to: string;
    label: string;
    icon: any;
    group: string;
    groupKey: string;
};

type NavGroup = {
    title: string;
    subtitle?: string;
    key: string;
    items: NavItemConfig[];
    moduleCode?: string;
};

type VersionInfo = {
    version: string;
    buildTime: string;
    commit: string;
};

export function getCollapsedVersionLabel(version: string): string {
    const segments = version.trim().split(/[.+_-]/).filter(Boolean);
    const lastSegment = segments[segments.length - 1] || version.trim();
    return lastSegment.length > 0 && lastSegment.length <= 5 ? lastSegment : 'v';
}

export function getVersionTooltip(version: VersionInfo): string {
    const commit = version.commit && version.commit !== 'unknown' ? ` • ${version.commit}` : '';
    return `v${version.version} • ${version.buildTime}${commit}`;
}

const ROLE_BOOSTS: Record<string, Record<string, number>> = {
    admin: {},
    sales: {
        '/inquiries': 5,
        '/legacy/estimates': 5,
        '/sales/orders': 5,
        '/sales/invoices': 4,
        '/delivery': 3,
    },
    purchasing: {
        '/purchasing/orders': 5,
        '/purchasing/grn': 5,
        '/purchasing/bills': 5,
    },
    finance: {
        '/financials': 5,
        '/sales/invoices': 5,
        '/reports': 3,
    },
    production: {
        '/manufacturing': 5,
        '/inventory': 5,
        '/materials': 4,
    },
};

const GROUP_SUBTITLE: Record<string, string> = {
    sales: 'Pipeline to invoice',
    purchasing: 'Source to supplier payment',
    inventory: 'Stock, valuation, and materials',
    production: 'Work orders and execution',
    finance: 'Accounting and reporting',
    system: 'Configuration and support',
    general: 'Shared entry points',
};

const logoUrl = `${import.meta.env.BASE_URL}logo.jpg`;

export function buildFrequentItems(
    allItems: NavItemConfig[],
    recentPaths: Array<{ path: string; label: string; last_used_at: string }>,
    role: string,
    pinned: Set<string>
): NavItemConfig[] {
    const roleBoostMap = ROLE_BOOSTS[role] || {};
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000;

    const byPath = new Map<string, { item: NavItemConfig; score: number; count: number }>();
    for (const item of allItems) {
        const key = canonicalizeNavigationPath(item.to);
        byPath.set(key, { item, score: roleBoostMap[item.to] || roleBoostMap[key] || 0, count: 0 });
    }

    recentPaths.forEach((entry, idx) => {
        const row = byPath.get(canonicalizeNavigationPath(entry.path));
        if (!row) return;
        const ts = Date.parse(entry.last_used_at || '');
        const recencyWeight = Number.isFinite(ts) && ts >= cutoff ? Math.max(0, 12 - idx) : 0;
        row.count += 1;
        row.score += recencyWeight + row.count;
    });

    return Array.from(byPath.values())
        .filter((row) => !pinned.has(canonicalizeNavigationPath(row.item.to)) && row.score > 0)
        .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
        .slice(0, 8)
        .map((row) => row.item);
}

export function Sidebar({
    version,
    className,
}: {
    version?: VersionInfo | null;
    className?: string;
}) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
    const { user, logout, hasModuleAccess } = useAuth();
    const { getSetting } = useSettings();
    const { activeModules, modules } = useModuleLoader();
    const enabledModuleCodes = useMemo(() => new Set(activeModules.map((module) => module.id)), [activeModules]);

    const navigationV2Enabled = isEnabledFlag(getSetting('ui_navigation_v2_enabled'));
    const {
        profile,
        togglePinnedPath,
        trackNavigation,
    } = useNavigationProfile(navigationV2Enabled);

    const ICON_MAP: Record<string, any> = {
        LayoutDashboard,
        Users,
        Package,
        FileText,
        Calculator,
        ClipboardList,
        List,
        TrendingUp,
        UserCircle,
        ShoppingCart,
        ShoppingBag,
        DollarSign,
        Truck,
        BookOpen,
        Menu,
        LogOut,
        Zap,
        Factory,
        HelpCircle,
        Box,
        Layers,
        Receipt,
        CreditCard,
    };

    const allModuleDefinitions = [
        ...activeModules,
        ...modules
            .filter((m) => enabledModuleCodes.has(m.id) && !activeModules.find((am) => am.id === m.id))
            .map((m) => ({
                id: m.id,
                name: m.name,
                menus: m.menus || [],
                enabled: true,
            })),
    ];

    const moduleGroups: NavGroup[] = allModuleDefinitions.map((m) => ({
        title: m.name,
        subtitle: GROUP_SUBTITLE[m.id],
        key: m.id,
        moduleCode: m.id,
        items: (m.menus || []).map((menu) => ({
            to: menu.path,
            label: menu.label,
            icon: ICON_MAP[menu.icon] || Package,
            group: m.name,
            groupKey: m.id,
        })),
    }));

    const systemGroup: NavGroup = {
        title: 'System',
        subtitle: GROUP_SUBTITLE.system,
        key: 'system',
        items: [
            { to: '/settings', label: 'Settings', icon: UserCircle, group: 'System', groupKey: 'system' },
            ...(user?.role === 'Admin'
                ? [{ to: '/admin/modules', label: 'Modules', icon: Package, group: 'System', groupKey: 'system' }]
                : []),
            { to: '/help', label: 'Help Center', icon: BookOpen, group: 'System', groupKey: 'system' },
        ],
    };

    const generalGroup: NavGroup = {
        title: 'General',
        subtitle: GROUP_SUBTITLE.general,
        key: 'general',
        items: [
            { to: '/', label: 'Home', icon: LayoutDashboard, group: 'General', groupKey: 'general' },
            { to: '/reports', label: 'Reports', icon: TrendingUp, group: 'General', groupKey: 'general' },
        ],
    };

    const allowedModuleGroups = moduleGroups.filter((group) => {
        if (!group.moduleCode) return true;
        return enabledModuleCodes.has(group.moduleCode) && hasModuleAccess(group.moduleCode);
    });

    const groups: NavGroup[] = [generalGroup, ...allowedModuleGroups, systemGroup].filter((group) => group.items.length > 0);
    const moduleSectionGroups: NavGroup[] = [generalGroup, ...allowedModuleGroups].filter((group) => group.items.length > 0);

    const allItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
    const canonicalPinnedPaths = useMemo(
        () => new Set((profile.pinned_paths || []).map((path) => canonicalizeNavigationPath(path))),
        [profile.pinned_paths]
    );

    const pinnedItems = useMemo(
        () =>
            (profile.pinned_paths || [])
                .map((path) => findNavigationMatchByPath(allItems, path))
                .filter((item): item is NavItemConfig => Boolean(item))
                .filter((item, index, list) => list.findIndex((candidate) => candidate.to === item.to) === index),
        [allItems, profile.pinned_paths]
    );

    const frequentItems = useMemo(
        () =>
            buildFrequentItems(
                allItems,
                profile.recent_paths || [],
                String(user?.role || 'admin').toLowerCase(),
                canonicalPinnedPaths
            ),
        [allItems, canonicalPinnedPaths, profile.recent_paths, user?.role]
    );

    const showLegacy = !navigationV2Enabled;

    const handlePinToggle = async (path: string) => {
        if (!navigationV2Enabled) return;
        await togglePinnedPath(path);
    };

    // Accordion logic is now handled directly in the render loop
    const versionTooltip = version ? getVersionTooltip(version) : undefined;

    return (
        <div
            className={cn(
                'flex h-screen shrink-0 flex-col bg-gradient-to-b from-slate-900 to-slate-950 text-white shadow-xl transition-all duration-300 ease-in-out',
                isCollapsed ? 'w-20' : 'w-72',
                className
            )}
        >
            <div className="h-20 border-b border-slate-800/50 bg-white flex items-center justify-center px-2 relative">
                {!isCollapsed ? (
                    <div className="flex flex-col items-center justify-center w-full h-full py-1">
                        <img
                            src={logoUrl}
                            alt="Hadaf ERP"
                            className="h-14 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setIsCollapsed(true)}
                            title="Click to collapse sidebar"
                        />
                    </div>
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-white/5 transition-colors rounded-lg"
                        onClick={() => setIsCollapsed(false)}
                        title="Click to expand sidebar"
                    >
                        <Menu size={24} className="text-slate-800" />
                    </div>
                )}
            </div>

            <nav
                className={cn(
                    'flex-1 py-6 space-y-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                    isCollapsed ? 'overflow-visible px-2' : 'overflow-y-auto px-4'
                )}
            >
                {!showLegacy && pinnedItems.length > 0 && (
                    <NavSection
                        title="Pinned"
                        icon={<Pin size={22} />}
                        emptyText="Pin your must-use pages for one-click access."
                        isCollapsed={isCollapsed}
                        sectionKey="pinned"
                        expandedModules={expandedModules}
                        setExpandedModules={setExpandedModules}
                        totalItems={pinnedItems.length}
                    >
                        {pinnedItems.map((item) => (
                            <NavItem
                                key={`pin-${item.to}`}
                                to={item.to}
                                icon={<item.icon size={18} />}
                                label={item.label}
                                isCollapsed={false}
                                isPinned
                                onPinToggle={() => handlePinToggle(item.to)}
                                onNavigate={() => trackNavigation(item.to, item.label, 'pinned')}
                            />
                        ))}
                    </NavSection>
                )}

                {!showLegacy && (
                    <NavSection
                        title="Frequent"
                        icon={<Zap size={22} />}
                        emptyText="Start using pages; we'll pin your top workflows here."
                        isCollapsed={isCollapsed}
                        sectionKey="frequent"
                        expandedModules={expandedModules}
                        setExpandedModules={setExpandedModules}
                        totalItems={frequentItems.length}
                    >
                        {frequentItems.map((item) => (
                            <NavItem
                                key={`freq-${item.to}`}
                                to={item.to}
                                icon={<item.icon size={18} />}
                                label={item.label}
                                isCollapsed={false}
                                isPinned={canonicalPinnedPaths.has(canonicalizeNavigationPath(item.to))}
                                onPinToggle={() => handlePinToggle(item.to)}
                                onNavigate={() => trackNavigation(item.to, item.label, 'frequent')}
                            />
                        ))}
                    </NavSection>
                )}

                <div className={cn('space-y-2', isCollapsed ? 'pt-2' : 'pt-4')}>
                    {!isCollapsed && <h3 className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Modules</h3>}
                    {(showLegacy ? groups : moduleSectionGroups).map((group, index) => {
                        const hasState = Object.keys(expandedModules).length > 0;
                        const expanded = hasState ? !!expandedModules[group.key] : index === 0 && !pinnedItems.length && !frequentItems.length;

                        const visibleItems = showLegacy || expanded || isCollapsed ? group.items : [];
                        const totalItems = group.items.length;
                        const GroupIcon = group.items.length > 0 ? group.items[0].icon : Package;

                        return (
                            <NavSection
                                key={group.key}
                                title={group.title}
                                icon={<GroupIcon size={22} />}
                                isCollapsed={isCollapsed}
                                sectionKey={group.key}
                                expandedModules={expandedModules}
                                setExpandedModules={setExpandedModules}
                                totalItems={totalItems}
                            >
                                {visibleItems.map((item: NavItemConfig) => (
                                    <NavItem
                                        key={item.to}
                                        to={item.to}
                                        icon={<item.icon size={18} />}
                                        label={item.label}
                                        isCollapsed={false}
                                        isPinned={canonicalPinnedPaths.has(canonicalizeNavigationPath(item.to))}
                                        onPinToggle={() => handlePinToggle(item.to)}
                                        onNavigate={() => trackNavigation(item.to, item.label, 'module')}
                                    />
                                ))}
                            </NavSection>
                        );
                    })}
                </div>

                {!showLegacy && (
                    <NavSection
                        title="System"
                        icon={<UserCircle size={22} />}
                        isCollapsed={isCollapsed}
                        sectionKey="system"
                        expandedModules={expandedModules}
                        setExpandedModules={setExpandedModules}
                        totalItems={systemGroup.items.length}
                    >
                        {systemGroup.items.map((item) => (
                            <NavItem
                                key={`system-${item.to}`}
                                to={item.to}
                                icon={<item.icon size={18} />}
                                label={item.label}
                                isCollapsed={false}
                                isPinned={canonicalPinnedPaths.has(canonicalizeNavigationPath(item.to))}
                                onPinToggle={() => handlePinToggle(item.to)}
                                onNavigate={() => trackNavigation(item.to, item.label, 'system')}
                            />
                        ))}
                    </NavSection>
                )}
            </nav>

            <div className={cn('border-t border-slate-800/50 bg-slate-900/30', isCollapsed ? 'p-3' : 'p-4')}>
                <div
                    className={cn(
                        'flex items-center rounded-xl bg-white/5 p-3 hover:bg-white/10 transition-colors cursor-pointer border border-white/5',
                        isCollapsed ? 'justify-center' : 'gap-3'
                    )}
                >
                    <UserCircle className="h-9 w-9 text-slate-400 flex-shrink-0" />
                    {!isCollapsed && (
                        <div className="text-sm">
                            <div className="font-semibold text-slate-200">{user?.full_name || user?.username || 'User'}</div>
                            <div className="text-xs text-slate-500">{user?.role || 'Signed in'}</div>
                        </div>
                    )}
                </div>
                {!isCollapsed && (
                    <button
                        onClick={logout}
                        className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
                    >
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                )}
                {version && (
                    <div
                        className={cn(
                            'mt-3 rounded-lg border border-white/10 bg-white/5 text-[11px] text-slate-300',
                            isCollapsed
                                ? 'mx-auto flex h-8 w-12 items-center justify-center overflow-hidden px-1 text-center'
                                : 'min-w-0 px-2 py-1.5 text-left'
                        )}
                        title={versionTooltip}
                        aria-label={versionTooltip}
                    >
                        <span
                            className={cn(
                                'font-mono text-blue-300',
                                isCollapsed ? 'block max-w-full truncate text-[10px] leading-none' : 'break-all'
                            )}
                        >
                            {isCollapsed ? getCollapsedVersionLabel(version.version) : `v${version.version}`}
                        </span>
                        {!isCollapsed && (
                            <>
                                <span className="mx-1 text-slate-500">•</span>
                                <span>
                                    {new Date(version.buildTime).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </span>
                                {version.commit !== 'unknown' && version.commit !== 'local' && (
                                    <>
                                        <span className="mx-1 text-slate-500">•</span>
                                        <span className="font-mono text-slate-400">{version.commit}</span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function NavSection({
    title,
    icon,
    children,
    emptyText,
    isCollapsed,
    sectionKey,
    expandedModules,
    setExpandedModules,
    totalItems
}: {
    title: string;
    icon?: ReactNode;
    children: ReactNode;
    emptyText?: string;
    isCollapsed: boolean;
    sectionKey: string;
    expandedModules: Record<string, boolean>;
    setExpandedModules: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    totalItems?: number;
}) {
    const entries = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
    const hasState = Object.keys(expandedModules).length > 0;
    const isExpanded = hasState ? !!expandedModules[sectionKey] : sectionKey === 'pinned' || (sectionKey === 'frequent' && (totalItems || 0) > 0);

    if (isCollapsed) {
        return (
            <div className="group relative flex justify-center w-full py-1">
                <div className="text-slate-400 hover:text-white transition-colors flex items-center justify-center rounded-lg hover:bg-white/5 w-12 h-12">
                    {icon || <LayoutDashboard size={22} />}
                </div>
                {/* Invisible hover bridge: Huge vertical padding ensures stability when moving mouse */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 z-[100] hidden group-hover:block py-24 pl-0 pr-8 cursor-default">
                    <div className="bg-slate-900 border border-slate-700 shadow-2xl rounded-xl p-3 w-64 text-white animate-in fade-in zoom-in-95 duration-200">
                        <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2 border-b border-slate-800/50 pb-2">{title}</h4>
                        <div className="space-y-1">
                            {entries.length > 0 ? entries : emptyText ? <p className="px-2 text-xs text-slate-500">{emptyText}</p> : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {!isCollapsed && (
                <button
                    type="button"
                    className="w-full flex items-center justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300"
                    onClick={() => {
                        setExpandedModules((prev) => ({
                            ...prev,
                            [sectionKey]: !isExpanded,
                        }));
                    }}
                >
                    <span>
                        {title} {totalItems !== undefined && <span className="text-slate-600">({totalItems})</span>}
                    </span>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
            )}
            {isExpanded && (
                <div className="space-y-1">
                    {entries.length > 0 ? entries : !isCollapsed && emptyText ? <p className="px-2 text-xs text-slate-500">{emptyText}</p> : null}
                </div>
            )}
        </div>
    );
}

function NavItem({
    to,
    icon,
    label,
    isCollapsed,
    isPinned,
    onPinToggle,
    onNavigate,
}: {
    to: string;
    icon: React.ReactNode;
    label: string;
    isCollapsed: boolean;
    isPinned?: boolean;
    onPinToggle?: () => void;
    onNavigate?: () => void;
}) {
    const location = useLocation();

    // Custom active matching logic to handle flat routes and standard redirects
    const isActivePath = () => {
        if (location.pathname === to) return true;
        if (to !== '/' && location.pathname.startsWith(to)) return true;

        // Handle specific module path redirects (Purchasing)
        if (to === '/purchasing/orders' && location.pathname.startsWith('/legacy/purchase-orders')) return true;
        if (to === '/purchasing/bills' && location.pathname.startsWith('/legacy/supplier-bills')) return true;
        if (to === '/purchasing/grn' && location.pathname.startsWith('/legacy/grn')) return true;

        // Handle specific module path redirects (Sales/Legacy module paths)
        if (to === '/sales/inquiries' && location.pathname.startsWith('/legacy/inquiries')) return true;
        if (to === '/sales/orders' && location.pathname.startsWith('/legacy/orders')) return true;
        if (to === '/sales/delivery' && location.pathname.startsWith('/legacy/delivery')) return true;
        if (to === '/sales/invoices' && location.pathname.startsWith('/legacy/invoices')) return true;
        if (to === '/sales/estimates' && location.pathname.startsWith('/legacy/estimates')) return true;
        if (to === '/sales/customers' && location.pathname.startsWith('/legacy/customers')) return true;

        // Handle direct base redirects (e.g. /inquiries -> /legacy/inquiries)
        if (to === '/inquiries' && location.pathname.startsWith('/legacy/inquiries')) return true;
        if (to === '/orders' && location.pathname.startsWith('/legacy/orders')) return true;
        if (to === '/delivery' && location.pathname.startsWith('/legacy/delivery')) return true;
        if (to === '/invoices' && location.pathname.startsWith('/legacy/invoices')) return true;
        if (to === '/estimates' && location.pathname.startsWith('/legacy/estimates')) return true;
        if (to === '/customers' && location.pathname.startsWith('/legacy/customers')) return true;

        return false;
    };

    const isActuallyActive = isActivePath();

    return (
        <div className="group relative">
            <NavLink
                to={to}
                title={isCollapsed ? label : undefined}
                onClick={onNavigate}
                className={() =>
                    cn(
                        'flex items-center rounded-lg px-3 py-2 text-slate-400 transition-all duration-200',
                        'hover:bg-white/5 hover:text-slate-100',
                        isActuallyActive && 'bg-primary text-white font-medium shadow-lg shadow-primary/20 ring-1 ring-white/10',
                        isCollapsed ? 'justify-center' : 'gap-3'
                    )
                }
            >
                {icon}
                {!isCollapsed && <span className="text-sm pr-8">{label}</span>}
            </NavLink>
            {!isCollapsed && onPinToggle && (
                <button
                    type="button"
                    aria-label={isPinned ? `Unpin ${label}` : `Pin ${label}`}
                    className="absolute right-2 top-1.5 p-1 rounded text-slate-500 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onPinToggle();
                    }}
                >
                    {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                </button>
            )}
        </div>
    );
}
